<template>
  <div
    ref="app"
    class="chat-app"
    :class="{ 'chat-app--thread': !!conversation }"
    :style="fitStyle"
  >
    <chat-sidebar
      class="chat-app__side"
      :active-id="activeId"
      :starting="starting"
      @select-conversation="id => $store.dispatch('chat/select', id)"
      @select-contact="openContact"
    />

    <main class="chat-app__main">
      <chat-thread
        v-if="conversation"
        :key="conversation.id"
        :conversation="conversation"
        @back="$store.dispatch('chat/deselect')"
      />

      <div
        v-else
        class="chat-app__welcome"
      >
        <span class="chat-app__welcome-icon"><feather-icon
          icon="MessageCircleIcon"
          size="42"
        /></span>
        <h3>{{ $t('chat.welcomeTitle') }}</h3>
        <p>{{ $t('chat.welcomeText') }}</p>
        <small><feather-icon
          icon="LockIcon"
          size="13"
        /> {{ $t('chat.welcomeNote') }}</small>
      </div>
    </main>
  </div>
</template>

<script>
import ChatSidebar from '@/components/chat/ChatSidebar.vue'
import ChatThread from '@/components/chat/ChatThread.vue'
import { requestNotificationPermission } from '@/utils/chatNotify'

const MOBILE_QUERY = '(max-width: 767px)'
const BOTTOM_GAP = { mobile: 8, desktop: 24 }
const BODY_CLASS = 'chat-page'

export default {
  name: 'ChatIndex',

  components: { ChatSidebar, ChatThread },

  data() {
    return { starting: null, fitHeight: null }
  },

  computed: {
    // No celular a altura vem da viewport real (sem a barra do navegador e com o teclado aberto), não de um 100vh fixo.
    fitStyle() {
      return this.fitHeight ? { height: `${this.fitHeight}px` } : null
    },
    conversation() {
      return this.$store.getters['chat/activeConversation']
    },
    activeId() {
      return this.$store.state.chat.activeId
    },
  },

  created() {
    this.$store.dispatch('chat/start')
    requestNotificationPermission()
  },

  mounted() {
    document.body.classList.add(BODY_CLASS)
    this.fit()
    window.addEventListener('resize', this.fit)
    if (window.visualViewport) window.visualViewport.addEventListener('resize', this.fit)
  },

  beforeDestroy() {
    document.body.classList.remove(BODY_CLASS)
    window.removeEventListener('resize', this.fit)
    if (window.visualViewport) window.visualViewport.removeEventListener('resize', this.fit)

    // Sair da tela fecha a conversa: o contador de não lidas volta a contar as novas mensagens.
    this.$store.dispatch('chat/deselect')
  },

  methods: {
    fit() {
      this.$nextTick(() => {
        const { app } = this.$refs
        if (!app) return
        const mobile = window.matchMedia(MOBILE_QUERY).matches
        const viewport = window.visualViewport ? window.visualViewport.height : window.innerHeight
        // O chat ocupa exatamente o que sobra da tela (teclado já descontado): só a conversa rola, a página não.
        const top = app.getBoundingClientRect().top + window.scrollY
        const gap = mobile ? BOTTOM_GAP.mobile : BOTTOM_GAP.desktop
        this.fitHeight = Math.max(mobile ? 320 : 480, Math.floor(viewport - top - gap))
      })
    },

    async openContact(userId) {
      this.starting = userId
      try {
        await this.$store.dispatch('chat/openWithUser', userId)
      } catch (e) {
        const errors = e.response && e.response.data && e.response.data.errors
        this.$message(null, (errors && Object.values(errors)[0][0]) || this.$t('chat.errors.open'), 'error')
        // O contato pode ter saído: atualiza a lista.
        this.$store.dispatch('chat/loadContacts')
      } finally {
        this.starting = null
      }
    },
  },
}
</script>

<style scoped>
.chat-app {
  --chat-surface: #fff;
  --chat-header: #f7f7fb;
  --chat-input: #f0f0f6;
  --chat-border: #e6e5ec;
  --chat-text: #3b3a4a;
  --chat-muted: #7d7b8c;
  --chat-in: #fff;
  --chat-out: #e4e1fd;
  --chat-pill: rgba(255, 255, 255, 0.92);
  --chat-wallpaper: #efedf8 radial-gradient(rgba(115, 103, 240, 0.09) 1.2px, transparent 1.2px) 0 0 / 22px 22px;

  display: grid;
  grid-template-columns: minmax(300px, 380px) 1fr;
  height: calc(100vh - 11.5rem);
  min-height: 480px;
  border-radius: 12px;
  background: var(--chat-surface);
  box-shadow: 0 4px 24px rgba(34, 41, 47, 0.1);
  overflow: hidden;
}

.dark-layout .chat-app {
  --chat-surface: #283046;
  --chat-header: #2d3550;
  --chat-input: #343d55;
  --chat-border: #3b4253;
  --chat-text: #d0d2d6;
  --chat-muted: #9a9cae;
  --chat-in: #343d55;
  --chat-out: #4a3fb8;
  --chat-pill: rgba(40, 48, 70, 0.92);
  --chat-wallpaper: #1f2639 radial-gradient(rgba(255, 255, 255, 0.045) 1.2px, transparent 1.2px) 0 0 / 22px 22px;
}

.chat-app__main { min-width: 0; min-height: 0; }

.chat-app__welcome {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 100%;
  padding: 24px;
  background: var(--chat-wallpaper);
  color: var(--chat-muted);
  text-align: center;
}

.chat-app__welcome h3 { margin: 6px 0 0; color: var(--chat-text); font-weight: 600; }
.chat-app__welcome p { max-width: 380px; margin: 0; }
.chat-app__welcome small { margin-top: 18px; }

.chat-app__welcome-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 92px;
  height: 92px;
  border-radius: 50%;
  background: rgba(115, 103, 240, 0.14);
  color: #7367f0;
}

/* Celular/tablet estreito: uma coluna por vez (lista ou conversa), como no WhatsApp. */
@media (max-width: 767px) {
  .chat-app {
    grid-template-columns: 1fr;
    height: calc(100dvh - 9rem);
    min-height: 0;
    border-radius: 10px;
  }
  .chat-app--thread .chat-app__side { display: none; }
  .chat-app:not(.chat-app--thread) .chat-app__main { display: none; }
}
</style>

<style>
/* A página não rola nesta tela: a altura do chat é calculada para caber na janela. */
.chat-page { overflow: hidden; }

/* Celular: o título e o breadcrumb da página só roubam espaço da conversa. */
@media (max-width: 767px) {
  .chat-page .content-header { display: none; }
  .chat-page .content-wrapper { padding-top: 0.5rem; }
}
</style>
